import cn from "classnames";
import { Button } from "oneclick-component/src/components/Button";
import Pagination from "oneclick-component/src/components/Pagination";
import useShowMessage from "oneclick-component/src/hooks/useShowMessage";
import { DownloadIcon } from "oneclick-component/src/icon";
import { useBreakPoints } from "oneclick-component/src/providers";
import {
  BriefStation,
  ListWhatsappChannelStatsHandlerWhatsappChannelStatsListGetApiArg,
  useExportPaidMessageSentReportHandlerWhatsappChannelStatsListExportPostMutation as useExportPaidMessageSentReport,
  useListWhatsappChannelStatsHandlerWhatsappChannelStatsListGetQuery,
  WhatsappChannelStats,
} from "oneclick-component/src/store/apis/enhancedApi";
import {
  formatDateTime,
  isoToDateTime,
} from "oneclick-component/src/utils/datetime";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import MobileSearchView from "../../components/MobileSearchView";
import { useIsManager, useIsSuperAdmin } from "../../hooks/role";
import { useSelectedStationProfile } from "../../hooks/useSelectedStationProfile";
import { useShowError } from "../../hooks/useShowError";
import PaidMessageSentActionBar from "./ActionBar";
import Table from "./Table";
import {
  PaidMessageSentListRecord,
  usePaidMessageSentListTable,
} from "./useTable";
import { constructProfileScopedWorkingStationFilter } from "./workingStationFilter";

const PAGE_SIZE = 25;
const transformToTableData = (
  s: WhatsappChannelStats
): PaidMessageSentListRecord => {
  return {
    name: s.ptUser.name,
    // dedup phone numbers as they should always the same
    // among all users having the same recipient whatsapp
    // id
    phoneNumber: s.ptUser.phoneNumber,
    station: s.station.shortCode,
    lastReplyTimestamp: s.lastReplyTimestamp
      ? formatDateTime(
          isoToDateTime(s.lastReplyTimestamp),
          "dd-LL-yyyy HH:mm 'HKT'"
        )
      : null,
    paidMessageSent: s.paidMessageSent,
  };
};

const PaidMessageSentScreen = (): ReactElement => {
  const { showError } = useShowError();
  const { useIsSm } = useBreakPoints();
  const isSm = useIsSm();
  const isManager = useIsManager();
  const isSuperAdmin = useIsSuperAdmin();
  const showMessage = useShowMessage();
  const { t } = useTranslation();

  const [pageIndex, setPageIndex] = useState<number>(0);

  const [searchString, setSearchString] = useState<string | undefined>(
    undefined
  );

  const [stationId, setStationId] = useState<number | null>(null);

  const selectedStationProfile = useSelectedStationProfile();

  const profileScopedWorkingStationFilter =
    constructProfileScopedWorkingStationFilter(
      selectedStationProfile,
      stationId
    );

  const whatsappChannelStatsQueryParams =
    useMemo<ListWhatsappChannelStatsHandlerWhatsappChannelStatsListGetApiArg>(
      () => ({
        pageIndex,
        pageSize: PAGE_SIZE,
        searchString,
        stationId: profileScopedWorkingStationFilter,
      }),
      [pageIndex, profileScopedWorkingStationFilter, searchString]
    );

  const {
    currentData: response,
    isFetching: isLoading,
    error,
    data: cachedResponse,
  } = useListWhatsappChannelStatsHandlerWhatsappChannelStatsListGetQuery(
    whatsappChannelStatsQueryParams
  );

  const [
    exportPaidMessageSentReport,
    { isLoading: exportPaidMessageSentReportIsLoading },
  ] = useExportPaidMessageSentReport();

  const regularTotalCount = cachedResponse?.totalCount ?? 0;
  const results = response?.results;
  const tableData = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return response?.results.map(transformToTableData) ?? [];
  }, [isLoading, response?.results]);
  const isEmpty = tableData.length === 0;

  const onChangeSearchString = useCallback((v: string) => {
    const trimmedSearchString = v.trim();
    if (trimmedSearchString === "") {
      setSearchString(undefined);
      return;
    }
    setSearchString(trimmedSearchString);
  }, []);

  const onChangeStationFilter = useCallback((station: BriefStation | null) => {
    setStationId(station?.id ?? null);
  }, []);

  const handleOnClickPage = useCallback((pageIndex: number) => {
    setPageIndex(pageIndex);
  }, []);
  const [isMobileSearchView, setIsMobileSearchView] = useState<boolean>(false);

  const onEnterMobileSearchView = useCallback(
    () => setIsMobileSearchView(true),
    []
  );
  const onExitMobileSearchView = useCallback(
    () => setIsMobileSearchView(false),
    []
  );

  const onClickExportPaidMessageSentReport = useCallback(() => {
    exportPaidMessageSentReport(whatsappChannelStatsQueryParams)
      .unwrap()
      .then(() => {
        showMessage({
          type: "info",
          title: t("paidMessageSentList.exportPaidMessageSentReport.started"),
        });
      })
      .catch(() => {
        showMessage({
          type: "fail",
          title: t("paidMessageSentList.exportPaidMessageSentReport.failed"),
        });
      });
  }, [
    exportPaidMessageSentReport,
    whatsappChannelStatsQueryParams,
    showMessage,
    t,
  ]);

  useEffect(() => {
    if (error != null) {
      showError(error);
    }
  }, [error, showError]);

  const table = usePaidMessageSentListTable(tableData);

  const contentUnderSearchBar = useMemo(
    () => (
      <>
        <div className={cn("mb-6", "flex", "flex-col", "gap-6")}>
          <PaidMessageSentActionBar
            searchString={searchString}
            onChangeSearchString={onChangeSearchString}
            showStationFilter={!isManager}
            stationFilter={stationId}
            onChangeStationFilter={onChangeStationFilter}
            isMobileSearchView={isMobileSearchView}
            onEnterMobileSearchView={onEnterMobileSearchView}
          />
          {isSuperAdmin ? (
            <div className={cn("flex", "flex-row", "h-8", "justify-end")}>
              <Button
                theme="whiteNoFillBlackTextThick"
                prefixIcon={DownloadIcon}
                className={cn("h-8", "text-xs", "font-medium")}
                iconClassName="!w-3"
                disabled={exportPaidMessageSentReportIsLoading}
                onClick={onClickExportPaidMessageSentReport}
              >
                <Trans i18nKey="paidMessageSentList.exportMenu.exportPaidMessageSentReport" />
              </Button>
            </div>
          ) : null}
        </div>
        <Table table={table} isEmpty={isEmpty} isLoading={isLoading} />
        {regularTotalCount > 0 || (results != null && results.length > 0) ? (
          <>
            <Pagination
              className={cn(
                "mt-3",
                "bg-white",
                !isSm && ["fixed", "bottom-0", "inset-x-0"]
              )}
              totalPages={Math.ceil(regularTotalCount / PAGE_SIZE)}
              pageIndex={pageIndex}
              onClickPage={handleOnClickPage}
            />
            <div className="h-16" />
          </>
        ) : null}
      </>
    ),
    [
      searchString,
      onChangeSearchString,
      isManager,
      stationId,
      onChangeStationFilter,
      isMobileSearchView,
      onEnterMobileSearchView,
      isSuperAdmin,
      exportPaidMessageSentReportIsLoading,
      onClickExportPaidMessageSentReport,
      table,
      isEmpty,
      isLoading,
      regularTotalCount,
      results,
      isSm,
      pageIndex,
      handleOnClickPage,
    ]
  );

  return (
    <main>
      <div
        className={cn(
          "rounded-lg",
          "bg-white",
          "mx-auto",
          "px-6",
          "pt-6",
          "pb-20",
          "sm:pb-6",
          "sm:mb-6",
          "space-y-5"
        )}
      >
        <div className="flex">
          <p
            className={cn("text-black/86", "font-medium", "text-xl", "flex-1")}
          >
            <Trans i18nKey="paidMessageSentList.title" />
          </p>
        </div>
        {isMobileSearchView ? (
          <MobileSearchView
            onChangeSearchInput={onChangeSearchString}
            onExit={onExitMobileSearchView}
          >
            {contentUnderSearchBar}
          </MobileSearchView>
        ) : (
          <div className="mt-1">{contentUnderSearchBar}</div>
        )}
      </div>
    </main>
  );
};

export default PaidMessageSentScreen;
